import { useMemo } from "react";
import Icon from "../Icon";
import { Avatar } from "../Atoms";
import { diasDelPeriodo } from "../../utils";
import { horasDeTramo } from "../../horasTramo";
import { JORNADA_DEFECTO, objetivoDia, fmtH } from "../../jornada";

// Lo que ha hecho cada persona del equipo este mes, y lo que se le pedía.
//
// Es la cuenta que hasta ahora había que sacar contando celdas del calendario:
// horas fichadas, días con fichaje, horas exigibles y la diferencia. Recibe las
// filas YA filtradas por equipo, así que enseña exactamente lo que se ve arriba.

const fmtHoras = h => {
  const m = Math.max(0, Math.round(h * 60));
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};
const fmtSaldo = h => `${h < 0 ? "−" : "+"}${fmtHoras(Math.abs(h))}`;

// Horas de un conjunto de fichajes, contando cada tramo UNA vez.
//
// Un tramo de Odoo son dos filas nuestras —entrada y salida— y las dos llevan
// las horas trabajadas del tramo. Sumar la columna a pelo las contaba dos veces
// y un día de 8 h salía como 16. Tampoco vale quedarse con el máximo de las dos:
// tras una corrección a la baja en Odoo, la fila que no se haya refrescado
// ganaba siempre. Las horas salen de una sola fila por tramo (horasTramo.js), la
// misma que usa el detalle del día.
export const horasReales = filas => {
  const porTramo = new Map();
  for (const f of filas) {
    if (f.horas === null || f.horas === undefined) continue;
    // Sin id de asistencia no hay tramo con el que agrupar: cada fila va sola.
    const clave = f.odoo_attendance_id ? `a${f.odoo_attendance_id}` : `f${f.id}`;
    const t = porTramo.get(clave) || { entrada: null, salida: null };
    if (f.accion === "salida") t.salida = f; else t.entrada = f;
    porTramo.set(clave, t);
  }
  let total = 0;
  for (const { entrada, salida } of porTramo.values()) total += horasDeTramo(entrada, salida) || 0;
  return total;
};

export default function ResumenEquipo({ filas = [], users = [], anio, mes,
                                        festivos = {}, vacaciones = [],
                                        equipoDe = null, jornadaDe = null }) {
  const hoyIso = new Intl.DateTimeFormat("sv-SE", { timeZone:"Europe/Madrid" }).format(new Date());
  const esMesActual = hoyIso.slice(0, 7) === `${anio}-${String(mes + 1).padStart(2, "0")}`;

  // Días del mes con su clave ISO, para no repetir el bucle por persona.
  const diasMes = useMemo(() => {
    const ultimo = new Date(anio, mes + 1, 0).getDate();
    return Array.from({ length: ultimo }, (_, i) => {
      const d = i + 1;
      return `${anio}-${String(mes + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    });
  }, [anio, mes]);

  // Las ausencias de cada persona en este mes, en un set de días.
  //
  // Solo las APROBADAS descuentan horas exigibles, que es lo que dice el
  // esquema (backend/schema.sql, tabla vacaciones): un borrador sin enviar o una
  // peticion que RRHH ha denegado no eximen de la jornada, y contarlas le
  // regalaba el saldo a quien solo habia pedido los dias.
  const ausenciasDe = useMemo(() => {
    const m = {};
    vacaciones.forEach(v => {
      if (v.estado && v.estado !== "aprobada") return;
      const uid = String(v.user_id);
      diasDelPeriodo(v.fecha, v.fecha_fin).forEach(d => {
        if (!d.startsWith(`${anio}-${String(mes + 1).padStart(2, "0")}`)) return;
        (m[uid] = m[uid] || new Set()).add(d);
      });
    });
    return m;
  }, [vacaciones, anio, mes]);

  const filasPorPersona = useMemo(() => {
    const m = {};
    filas.forEach(f => { (m[String(f.user_id)] = m[String(f.user_id)] || []).push(f); });
    return m;
  }, [filas]);

  // Una fila por persona con fichajes en el mes. Quien no tiene ninguno no
  // aparece: el registro no sabe si estaba de baja o si nadie fichó por él, y
  // apuntarle 0 h sobre 160 sería acusarle de algo que no consta.
  const personas = useMemo(() => Object.entries(filasPorPersona).map(([uid, suyas]) => {
    const ausencias = ausenciasDe[uid] || new Set();
    const jornada = jornadaDe?.(uid) || JORNADA_DEFECTO;
    // Lo exigible: las horas de la jornada de ESA persona cada día (Odoo, o 8 h de lunes a
    // viernes por defecto), sin festivos ni ausencias. En el mes en curso solo cuentan los días ya cerrados, que es
    // como se mide el saldo en el panel de jornada.
    let exigidas = 0, exigidasMes = 0;
    for (const dia of diasMes) {
      const obj = objetivoDia(jornada, dia);
      if (!obj || festivos[dia] || ausencias.has(dia)) continue;
      exigidasMes += obj;
      if (!esMesActual || dia < hoyIso) exigidas += obj;
    }
    const horas = horasReales(suyas);
    const dias = new Set(suyas.map(f => f.dia)).size;
    const fallos = suyas.filter(f => f.odoo_estado !== "ok").length;
    return {
      uid,
      nombre: suyas[0].user_name || users.find(u => String(u.id) === uid)?.name || "—",
      equipo: equipoDe?.(uid) || null,
      horas, dias, exigidas, exigidasMes, fallos, jornada,
      saldo: horas - exigidas,
      ausencias: ausencias.size,
    };
  }).sort((a, b) => a.saldo - b.saldo), [filasPorPersona, ausenciasDe, diasMes, festivos, esMesActual, hoyIso, users, jornadaDe]);

  // Lo mismo por equipos. Con dos o mas departamentos delante, la tabla por
  // persona no responde a la pregunta que se viene a hacer aqui —que
  // departamento va corto y cual va sobrado—: habia que sumar filas a mano.
  //
  // Solo aparece con mas de un equipo: con uno, la barra seria el total que ya
  // esta en la ultima linea de la tabla.
  const equiposCmp = useMemo(() => {
    if (!equipoDe) return [];
    const m = new Map();
    for (const p of personas) {
      const clave = p.equipo?.name || "Sin equipo";
      const acc = m.get(clave)
        || { nombre: clave, color: p.equipo?.color || "var(--text3)",
             horas:0, exigidas:0, personas:0, dias:0 };
      acc.horas += p.horas; acc.exigidas += p.exigidas;
      acc.dias += p.dias; acc.personas++;
      m.set(clave, acc);
    }
    return [...m.values()]
      .map(e => ({ ...e, saldo: e.horas - e.exigidas, media: e.horas / e.personas }))
      .sort((a, b) => b.media - a.media);
  }, [personas, equipoDe]);

  if (!personas.length) return null;

  const total = personas.reduce((s, p) => ({
    horas: s.horas + p.horas, exigidas: s.exigidas + p.exigidas, dias: s.dias + p.dias,
  }), { horas:0, exigidas:0, dias:0 });
  const saldoTotal = total.horas - total.exigidas;
  // Las barras se miden contra el equipo que mas horas por persona lleva: es una
  // comparacion entre ellos, no contra un objetivo absoluto.
  const techoCmp = Math.max(...equiposCmp.map(e => e.media), 1);

  const card = { background:"var(--surface)", borderRadius:13, border:"1px solid var(--border)",
                 boxShadow:"var(--shadow-sm)" };
  const th = { fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em",
               color:"var(--text3)", padding:"0 8px 6px", textAlign:"right", whiteSpace:"nowrap" };
  const td = { fontSize:12, padding:"6px 8px", textAlign:"right", whiteSpace:"nowrap",
               fontVariantNumeric:"tabular-nums" };

  return (
    <div style={{ ...card, padding:"13px 14px" }}>
      <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:9 }}>
        <span style={{ fontWeight:800, fontSize:13 }}>Resumen del mes</span>
        <span style={{ fontSize:11, color:"var(--text3)" }}>
          {personas.length} persona{personas.length === 1 ? "" : "s"} con fichajes
          {esMesActual ? " · exigidas hasta ayer" : ""}
        </span>
      </div>

      {equiposCmp.length > 1 && (
        <div style={{ marginBottom:12, paddingBottom:11,
                      borderBottom:"1px solid var(--border2)" }}>
          <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase",
                        letterSpacing:".07em", color:"var(--text3)", marginBottom:7 }}>
            Horas por persona, equipo a equipo
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {equiposCmp.map(e => (
              <div key={e.nombre} style={{ display:"flex", alignItems:"center", gap:9 }}
                title={`${e.nombre}: ${fmtHoras(e.horas)} fichadas de ${e.exigidas} h exigidas`
                       + ` entre ${e.personas} persona${e.personas === 1 ? "" : "s"}`}>
                <span style={{ width:132, flexShrink:0, fontSize:11.5, fontWeight:600,
                               whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                  {e.nombre}
                </span>
                <span style={{ flex:1, minWidth:60, height:14, background:"var(--surface2)",
                               borderRadius:4, overflow:"hidden" }}>
                  <span style={{ display:"block", height:"100%", borderRadius:4,
                                 width:`${Math.max(2, (e.media / techoCmp) * 100)}%`,
                                 background: e.color }}/>
                </span>
                <span style={{ width:66, flexShrink:0, textAlign:"right", fontSize:11.5,
                               fontWeight:700, fontVariantNumeric:"tabular-nums" }}>
                  {fmtHoras(e.media)}
                </span>
                <span style={{ width:70, flexShrink:0, textAlign:"right", fontSize:11,
                               fontWeight:800, fontVariantNumeric:"tabular-nums",
                               color: e.saldo >= 0 ? "var(--ok-text)" : "var(--red-text)" }}>
                  {fmtSaldo(e.saldo)}
                </span>
                <span className="m-hide" style={{ width:74, flexShrink:0, textAlign:"right",
                               fontSize:11, color:"var(--text3)" }}>
                  {e.personas} pers.
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign:"left" }}>Persona</th>
              {equiposCmp.length > 1 && <th style={{ ...th, textAlign:"left" }}>Equipo</th>}
              <th style={th}>Horas fichadas</th>
              <th style={th}>Horas que tocaba</th>
              <th style={th}>Saldo</th>
              <th style={th}>Días</th>
              <th style={th}>Días de ausencia</th>
            </tr>
          </thead>
          <tbody>
            {personas.map(p => (
              <tr key={p.uid} style={{ borderTop:"1px solid var(--border2)" }}>
                <td style={{ ...td, textAlign:"left" }}>
                  <span style={{ display:"flex", alignItems:"center", gap:7 }}>
                    <Avatar name={p.nombre} size={20}/>
                    {/* Nombre y jornada en dos lineas: en una sola, a 390 px empujaban las columnas de horas fuera de la pantalla */}
                    <span style={{ display:"flex", flexDirection:"column", minWidth:0 }}>
                      <span style={{ fontWeight:600, maxWidth:150, overflow:"hidden", textOverflow:"ellipsis" }} title={p.nombre}>{p.nombre}</span>
                      <span style={{ fontSize:11, color:"var(--text3)", whiteSpace:"nowrap" }}
                        title={p.jornada.origen === "odoo" ? "Jornada de su calendario de Odoo"
                          : "Límite por defecto (8 h de lunes a viernes): no hay un calendario de Odoo fiable"}>
                        {fmtH(p.jornada.horas_semana)}/sem{p.jornada.origen === "odoo" ? "" : " · por defecto"}
                      </span>
                    </span>
                    {p.fallos > 0 && (
                      <span title={`${p.fallos} fichaje(s) que no llegaron a Odoo`}
                        style={{ display:"flex", alignItems:"center" }}>
                        <Icon name="exclamation-triangle-fill" size={9} color="var(--warn-text)"/>
                      </span>
                    )}
                  </span>
                </td>
                {equiposCmp.length > 1 && (
                  <td style={{ ...td, textAlign:"left" }}>
                    <span style={{ fontSize:11, fontWeight:700, padding:"1px 6px", borderRadius:20,
                                   border:`1px solid ${p.equipo?.color || "var(--border)"}`,
                                   color: p.equipo?.color || "var(--text3)", whiteSpace:"nowrap" }}>
                      {p.equipo?.name || "Sin equipo"}
                    </span>
                  </td>
                )}
                <td style={{ ...td, fontWeight:700 }}>{fmtHoras(p.horas)}</td>
                <td style={{ ...td, color:"var(--text3)" }}>
                  {p.exigidas} h
                  {esMesActual && p.exigidasMes !== p.exigidas && (
                    <span style={{ fontSize:11 }}> / {p.exigidasMes}</span>
                  )}
                </td>
                <td style={{ ...td, fontWeight:800,
                             color: p.saldo >= 0 ? "var(--ok-text)" : "var(--red-text)" }}>
                  {fmtSaldo(p.saldo)}
                </td>
                <td style={{ ...td, color:"var(--text2)" }}>{p.dias}</td>
                <td style={{ ...td, color:"var(--text3)" }}>{p.ausencias || "—"}</td>
              </tr>
            ))}
            <tr style={{ borderTop:"1.5px solid var(--border)" }}>
              <td style={{ ...td, textAlign:"left", fontWeight:800 }}>Total</td>
              {equiposCmp.length > 1 && <td style={td}/>}
              <td style={{ ...td, fontWeight:800 }}>{fmtHoras(total.horas)}</td>
              <td style={{ ...td, color:"var(--text3)" }}>{total.exigidas} h</td>
              <td style={{ ...td, fontWeight:800,
                           color: saldoTotal >= 0 ? "var(--ok-text)" : "var(--red-text)" }}>
                {fmtSaldo(saldoTotal)}
              </td>
              <td style={{ ...td, color:"var(--text2)" }}>{total.dias}</td>
              <td style={td}/>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ fontSize:11, color:"var(--text3)", marginTop:8, lineHeight:1.45 }}>
        Horas que tocaba: las horas de la jornada de cada persona (la de su calendario de Odoo; 8 h de
        lunes a viernes cuando no hay una fiable), descontando festivos del calendario y
        vacaciones, permisos o bajas de cada persona. Quien no tiene ningún fichaje en el mes no
        sale en la tabla.
      </div>
    </div>
  );
}
